/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';
import { cssMap, jsx } from '@compiled/react';
import { isFedRamp } from '@atlassian/atl-context';
import { type WrappedComponentProps, injectIntl } from 'react-intl-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { SwitcherWrapper, Skeleton, ManageButton, SkeletonWithOrgs } from '../../primitives';
import { type SwitcherItemType } from '../../../common/utils/links';
import { type JoinableSiteItemType } from '../../../cross-join/utils/cross-join-links';
import {
	analyticsAttributes,
	NavigationAnalyticsContext,
	SWITCHER_SUBJECT,
	SWITCHER_JOINABLE_SITES,
	SWITCHER_AVAILABLE_PRODUCTS,
	RenderTracker,
	ViewedTracker,
	NotRenderedTracker,
	getJoinableSitesRenderTracker,
	getCustomLinksRenderTracker,
	SWITCHER_CURRENT_ORG,
} from '../../../common/utils/analytics';
import { performanceNow } from '../../../common/utils/performance-now';
import {
	type ProviderResults,
	type SyntheticProviderResults,
	type FeatureMap,
	type GetExtendedAnalyticsAttributes,
	type Product,
	type Organisation,
	type DiscoverySection,
	type GenericShowFlag,
} from '../../../types';
import { CrossJoinSection } from '../../../cross-join/components/cross-join-section';
import { SwitchToSection } from './switch-to-section';
import { CustomLinksSection } from './custom-links-section';
import ErrorBoundary from '../error-boundary';
import EmptyStateContent from './empty-state-content';
import {
	type WithExperienceTrackerProps,
	withExperienceTracker,
} from '../../../common/utils/experience-tracker';
import OrganisationSelector from './organisation-selector';
import CurrentOrganisation from './current-organisation';
import CurrentUser from './current-user';
import { token } from '@atlaskit/tokens';
import { OrgContext } from '../org-provider';
import messages from './messages';
import { type SwitcherTeam25ExperimentCohort } from '../experiment-provider';
import { Box } from '@atlaskit/primitives/compiled';
import { fg } from '@atlaskit/platform-feature-flags';

export type SwitcherProps = {
	/**
	 * Whether all the contents have been loaded
	 */
	hasLoaded: boolean;
	/**
	 * Whether contents considered critical path have been loaded
	 */
	hasLoadedCritical: boolean;
	hasLoadedInstanceProviders?: boolean;
	licensedProductLinks: SwitcherItemType[];
	adminLinks: SwitcherItemType[];
	joinableSiteLinks?: JoinableSiteItemType[];
	customLinks: SwitcherItemType[];
	organisations?: Map<Organisation['id'], Organisation> | undefined;
	exploreProductsLink: string;
	manageLink?: string;
	product?: Product;
	isPartialResult?: boolean;
	/** A post office discovery section to be injected */
	discoverySection?: DiscoverySection;
	onClose?: () => void;
	rawProviderResults: ProviderResults & SyntheticProviderResults;
	features: FeatureMap;
	getExtendedAnalyticsAttributes: GetExtendedAnalyticsAttributes;
	cloudId?: string;
	userEmail?: string;
	showFlag?: GenericShowFlag<any>;
	switcherTeam25ExperimentCohort?: SwitcherTeam25ExperimentCohort;
};

const getAnalyticsContext = (itemsCount: number, currentOrg: boolean) => ({
	...analyticsAttributes({
		itemsCount,
		currentOrg,
	}),
});

const styles = cssMap({
	wrapper: {
		display: 'flex',
		flexDirection: 'row',
	},
	standalone: {
		maxWidth: '100vw',
		height: '80vh',
		overflow: 'hidden',
		position: 'relative',
		backgroundColor: token('elevation.surface.overlay'),
	},
	standaloneAfter: {
		content: '""',
		position: 'absolute',
		bottom: 0,
		left: 0,
		width: '100%',
		height: 16,
		background: `linear-gradient(180deg, transparent, ${token('elevation.surface.overlay')})`,
		borderBottomLeftRadius: 5,
		zIndex: 1,
	},
	refreshedStandalone: {
		borderRadius: token('border.radius.200'),
	},
	dynamicHeight: {
		maxHeight: '80vh',
	},
	switchToContainer: {
		paddingTop: token('space.200'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.100'),
	},
	currentOrgContainer: {
		marginLeft: token('space.100'),
	},
	currentOrgContainerRefreshed: {
		marginLeft: token('space.200'),
		marginBottom: token('space.200'),
		marginTop: token('space.200'),
	},
	manageButton: {
		paddingLeft: token('space.200'),
		paddingBottom: token('space.100'),
	},
});

type SwitcherState = {
	currentOrgId: string;
	activeOrgId: string;
	switchToContainerHeight: number | null;
	prevSelectedOrgId: string;
};

export class Switcher extends React.PureComponent<
	SwitcherProps & WithExperienceTrackerProps & WrappedComponentProps,
	SwitcherState
> {
	mountedAt?: number;

	static contextType = OrgContext;
	context!: React.ContextType<typeof OrgContext>;

	static defaultProps = {};

	switchToContainerRef: React.RefObject<HTMLDivElement>;

	constructor(props: SwitcherProps & WithExperienceTrackerProps & WrappedComponentProps) {
		super(props);

		this.switchToContainerRef = React.createRef<HTMLDivElement>();
		this.state = {
			currentOrgId: '',
			activeOrgId: '',
			switchToContainerHeight: null,
			prevSelectedOrgId: '',
		};
	}

	setSwitchToContainerHeight = () => {
		if (this.switchToContainerRef.current) {
			// we add 12 to the height to account for the margin that is not included in 'this.switchToContainerRef.current.scrollHeight'
			const height = this.switchToContainerRef.current.scrollHeight + 12;
			this.setState({ switchToContainerHeight: height });
		}
	};

	componentDidMount() {
		this.mountedAt = performanceNow();
		this.setSwitchToContainerHeight();
	}

	componentDidUpdate() {
		this.context[1](this.state.activeOrgId);
		this.setSwitchToContainerHeight();
	}

	static getDerivedStateFromProps(props: SwitcherProps, state: SwitcherState) {
		if (!state.activeOrgId) {
			const currentSite = props.cloudId
				? props.rawProviderResults?.availableProducts?.data?.sites?.find(
						(site) => site.cloudId === props.cloudId,
					)
				: null;

			const currentOrg = currentSite?.org ? props.organisations?.get(currentSite.org.id) : null;

			if (currentOrg) {
				return {
					currentOrgId: currentOrg.id,
					activeOrgId: currentOrg.id,
				};
			}
		}

		return null;
	}

	timeSinceMounted(): number {
		return this.mountedAt !== undefined ? Math.round(performanceNow() - this.mountedAt) : 0;
	}

	setActiveOrg = (id: string) => {
		const prevSelectedOrgId = this.state.activeOrgId;
		this.setState({
			prevSelectedOrgId: prevSelectedOrgId,
			activeOrgId: id,
		});
	};

	render() {
		const {
			licensedProductLinks,
			isPartialResult,
			adminLinks,
			customLinks,
			exploreProductsLink,
			manageLink,
			hasLoaded,
			hasLoadedCritical,
			hasLoadedInstanceProviders,
			rawProviderResults,
			onClose,
			getExtendedAnalyticsAttributes,
			product,
			discoverySection,
			organisations,
			intl,
			showFlag,
			switcherTeam25ExperimentCohort,
		} = this.props;

		const isVisualRefreshEnabled = switcherTeam25ExperimentCohort === 'test';

		/**
		 * It is essential that switchToLinks reflects the order corresponding nav items
		 * are rendered below in the 'Switch to' section.
		 */
		const switchToLinks = [...licensedProductLinks, ...adminLinks];

		const locale = intl.locale.toLowerCase();
		const isJoinableProductsExperimentGateEnabled =
			(locale === 'en' || locale.startsWith('en-')) &&
			FeatureGates.checkGate('joinable_products_in_switcher_gate');

		const isDiscoveryInEmptyGateEnabled = fg('enable_xflow_in_empty_switcher');

		const joinableSiteLinks =
			isJoinableProductsExperimentGateEnabled && this.props.joinableSiteLinks
				? this.props.joinableSiteLinks?.filter((site) => site.cloudId === this.props.cloudId)
				: this.props.joinableSiteLinks || [];

		const itemsCount = switchToLinks.length + customLinks.length + joinableSiteLinks.length;

		const isEmptyState = !isPartialResult && itemsCount === 0;

		const firstContentArrived = Boolean(licensedProductLinks.length);

		let numberOfSites = firstContentArrived ? 1 : 0;
		if (licensedProductLinks) {
			const uniqueSets: { [key: string]: boolean } = {};
			licensedProductLinks.forEach((link) => {
				(link.childItems || []).forEach((item) => {
					uniqueSets[item.label] = true;
				});
			});

			const numbberOfUniqueSites = Object.keys(uniqueSets).length;
			if (numbberOfUniqueSites > 0) {
				numberOfSites = numbberOfUniqueSites;
			}
		}

		const multiOrgEnabled = organisations && organisations?.size > 1;

		const selectedOrg = multiOrgEnabled ? organisations?.get(this.state.activeOrgId) : null;

		const isSwitcherDynamicHeight = fg('app_switcher_dynamic_height');

		const dynamicHeightTransitionStyles = {
			// this static style is added to fix third party vendor css overrides in WAC (https://hello.jira.atlassian.cloud/browse/NAVX-731)
			display: 'flex',
			height:
				isSwitcherDynamicHeight && this.state.switchToContainerHeight
					? `${this.state.switchToContainerHeight}px`
					: '80vh',
			transition: this.state.prevSelectedOrgId && 'height 0.3s ease-in-out',
		};

		return (
			<NavigationAnalyticsContext
				data={getAnalyticsContext(itemsCount, this.state.activeOrgId === this.state.currentOrgId)}
			>
				<div
					// This ID is important as a workaround to address the duplicated compiled CSS issue in the remote App Switcher
					// https://hello.jira.atlassian.cloud/browse/NAVX-414
					id="atlassian-app-switcher"
					role="dialog"
					aria-label={
						intl
							? intl.formatMessage(messages.switcherModalLabel)
							: messages.switcherModalLabel.defaultMessage
					}
					css={[
						styles.wrapper,
						styles.standalone,
						isVisualRefreshEnabled && styles.refreshedStandalone,
						isSwitcherDynamicHeight && styles.dynamicHeight,
					]}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={dynamicHeightTransitionStyles}
				>
					<div css={styles.standaloneAfter} />
					{multiOrgEnabled && (
						<ErrorBoundary product={product as Product} triggerSubject="orgSelector" hideFallbackUI>
							<OrganisationSelector
								activeOrgId={this.state.activeOrgId}
								handleSetActiveOrg={this.setActiveOrg}
								organisations={organisations}
								product={product}
								switchToSectionHeight={this.state.switchToContainerHeight}
							/>
						</ErrorBoundary>
					)}
					<SwitcherWrapper
						activeOrgId={selectedOrg ? this.state.activeOrgId : undefined}
						hasLoadedCritical={hasLoadedCritical}
					>
						<div
							css={[
								hasLoadedCritical && !isVisualRefreshEnabled && styles.switchToContainer,
								isSwitcherDynamicHeight && styles.dynamicHeight,
							]}
							ref={this.switchToContainerRef}
						>
							<React.Fragment>
								{selectedOrg ? (
									<ErrorBoundary
										product={product as Product}
										triggerSubject="currentOrgErrorBoundary"
										hideFallbackUI
									>
										<ViewedTracker
											subject={SWITCHER_CURRENT_ORG}
											data={{
												organisation: selectedOrg.id,
											}}
										/>
										<div
											css={[
												isVisualRefreshEnabled
													? styles.currentOrgContainerRefreshed
													: styles.currentOrgContainer,
											]}
										>
											<CurrentOrganisation {...selectedOrg} />
											{this.props.userEmail && (
												<CurrentUser {...selectedOrg} email={this.props.userEmail} />
											)}
										</div>
									</ErrorBoundary>
								) : (
									<NotRenderedTracker
										subject={SWITCHER_CURRENT_ORG}
										data={{ organisation: null }}
									/>
								)}
								{hasLoaded && (
									<React.Fragment>
										<ViewedTracker
											subject={SWITCHER_SUBJECT}
											data={{
												licensedProducts: licensedProductLinks.map((item) => item.productType),
												licensedProductsNewlyAdded: licensedProductLinks
													.filter((item) => item.shouldShowNewLozenge)
													.map((item) => item.productType),
												autoprovisionedProducts: licensedProductLinks
													.filter((item) => !!item.autoProvisionDate)
													.map((item) => item.productType),
												inactiveProducts: licensedProductLinks
													.filter((item) => !!item.isProductInactive)
													.map((item) => item.productType),
												adminLinks: adminLinks.map((item) => item.key),
												joinableSiteLinks: joinableSiteLinks.map((item) => item.key),
												joinableSiteProductLinks: joinableSiteLinks.map((item) => ({
													cloudId: item.key,
													product: item.productType,
												})),
												numOrganisations: organisations?.size || 0,
												numberOfSites,
											}}
										/>
										{joinableSiteLinks.length > 0 && (
											<ViewedTracker
												subject={SWITCHER_JOINABLE_SITES}
												data={{ duration: this.timeSinceMounted() }}
											/>
										)}
										{licensedProductLinks.length > 0 ? (
											<RenderTracker
												subject={SWITCHER_AVAILABLE_PRODUCTS}
												data={{ duration: this.timeSinceMounted() }}
											/>
										) : (
											<NotRenderedTracker
												subject={SWITCHER_AVAILABLE_PRODUCTS}
												data={{ duration: this.timeSinceMounted() }}
											/>
										)}
									</React.Fragment>
								)}
								{hasLoadedInstanceProviders &&
									getCustomLinksRenderTracker(
										rawProviderResults.customLinks,
										rawProviderResults.userSiteData,
										customLinks,
										{ duration: this.timeSinceMounted() },
									)}
								{firstContentArrived && (
									<RenderTracker
										subject={SWITCHER_SUBJECT}
										data={{ duration: this.timeSinceMounted() }}
									/>
								)}
								{hasLoadedCritical &&
									(isEmptyState ? (
										<ErrorBoundary
											product={product as Product}
											triggerSubject="emptyStateErrorBoundary"
										>
											<EmptyStateContent exploreProductsLink={exploreProductsLink} />
										</ErrorBoundary>
									) : (
										<SwitchToSection
											adminLinks={adminLinks}
											isPartialResult={isPartialResult || false}
											licensedProductLinks={licensedProductLinks}
											getExtendedAnalyticsAttributes={getExtendedAnalyticsAttributes}
											product={product as Product}
											experimentalJoinableSiteLinks={
												isJoinableProductsExperimentGateEnabled ? joinableSiteLinks : undefined
											}
											showFlag={showFlag}
										/>
									))}
							</React.Fragment>
							{/* Feature flag check to hide join section*/}
							{!isJoinableProductsExperimentGateEnabled && (
								<ErrorBoundary
									product={product as Product}
									triggerSubject="crossJoinErrorBoundary"
									hideFallbackUI
								>
									<CrossJoinSection joinableSiteLinks={joinableSiteLinks} onClose={onClose} />
									{hasLoadedCritical &&
										getJoinableSitesRenderTracker(
											rawProviderResults.joinableSites,
											joinableSiteLinks,
											{ duration: this.timeSinceMounted() },
										)}
								</ErrorBoundary>
							)}
							{/* xflow section managed by post office team */}
							{((isEmptyState && isDiscoveryInEmptyGateEnabled) || !isEmptyState) &&
								!isFedRamp() &&
								!!discoverySection && (
									<ErrorBoundary
										product={product as Product}
										triggerSubject="crossFlowErrorBoundary"
										hideFallbackUI
									>
										{discoverySection}
									</ErrorBoundary>
								)}
							<ErrorBoundary
								product={product as Product}
								triggerSubject="customLinksErrorBoundary"
								hideFallbackUI
							>
								<CustomLinksSection customLinks={customLinks} />
								{!hasLoadedCritical &&
									(fg('app-switcher-skeleton-with-org-item') ? <SkeletonWithOrgs /> : <Skeleton />)}
								{manageLink && (
									<Box xcss={styles.manageButton}>
										<ManageButton href={manageLink} />
									</Box>
								)}
							</ErrorBoundary>
						</div>
					</SwitcherWrapper>
				</div>
			</NavigationAnalyticsContext>
		);
	}
}

export default injectIntl(withExperienceTracker(Switcher), {
	enforceContext: false,
});
